import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Building2, Image as ImageIcon, Loader2, Save, Globe, Hash,
  Calendar, MapPin, PackageCheck, ShoppingCart, Euro, FileText,
  User, Briefcase, Mail, Phone, Plus, Trash2, Upload,
  ExternalLink, Gamepad2, Settings2
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import DashboardWrapper from '../../components/DashboardWrapper';
import useNavigationGuard from '../../hooks/useNavigationGuard';
import NavigationBlockerDialog from '../../components/NavigationBlockerDialog';

const RosetteIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z" fill="currentColor" strokeWidth="0" />
  </svg>
);

const PartnerEntreprise = () => {
  const { user, activeOrg, hasPermission } = useAuth();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  const { blocker } = useNavigationGuard(isDirty);

  // org_id résolu : activeOrg si disponible, sinon undefined (legacy fallback côté backend)
  const currentOrgId = activeOrg?.org_id;
  // Ref synchrone pour protéger contre les réponses obsolètes (closures async)
  const orgIdRef = useRef(currentOrgId);
  useEffect(() => { orgIdRef.current = currentOrgId; }, [currentOrgId]);
  // Permission granulaire : le bouton enregistrer est désactivé si l'utilisateur n'a pas org.profile.edit
  const canEditProfile = hasPermission('org.profile.edit');

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    logo: '',
    banniere: '',
    contacts: [],
    conditions_achat: '',
    process_commande: '',
    shipping_rules: [],
    payment_terms: { delai_jours: '', escompte_percent: '', encours_max: '', mode_paiement: '', commentaire: '' },
    precommandes_autorisees: false,
    rupture_commandable: false,
    livraison_partielle: false,
  });

  // Detect changes by comparing formData to loaded original
  useEffect(() => {
    if (originalData) {
      setIsDirty(JSON.stringify(formData) !== JSON.stringify(originalData));
    }
  }, [formData, originalData]);

  // ── Multi-org : rechargement au changement d'organisation ──
  useEffect(() => {
    if (!currentOrgId) {
      // Aucune organisation active → effacer les données, pas de requête
      setPartner(null);
      setFormData({
        nom: '', description: '', logo: '', banniere: '', contacts: [],
        conditions_achat: '', process_commande: '', shipping_rules: [],
        payment_terms: { delai_jours: '', escompte_percent: '', encours_max: '', mode_paiement: '', commentaire: '' },
        precommandes_autorisees: false, rupture_commandable: false, livraison_partielle: false,
      });
      setOriginalData(null);
      setIsDirty(false);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    loadPartnerData(currentOrgId, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  const loadPartnerData = async (orgId, signal) => {
    setLoading(true);
    try {
      const params = orgId ? { org_id: orgId } : {};
      const response = await api.get('/partners/me', { params, signal });
      // Réponse obsolète : l'org a changé pendant la requête
      if (signal?.aborted || orgIdRef.current !== orgId) return;
      const data = response.data;
      setPartner(data);

      let contacts = data.contacts || [];
      if (contacts.length === 0 && (data.contact_email || data.contact_telephone)) {
        contacts = [{ id: Date.now().toString(), nom: 'Contact principal', email: data.contact_email || '', telephone: data.contact_telephone || '', fonction: '' }];
      }

      const loadedForm = {
        nom: data.nom || '',
        description: data.description || '',
        logo: data.logo || '',
        banniere: data.banniere || '',
        contacts,
        conditions_achat: data.conditions_achat || '',
        process_commande: data.process_commande || '',
        shipping_rules: data.shipping_rules || [],
        payment_terms: data.payment_terms || { delai_jours: '', escompte_percent: '', encours_max: '', mode_paiement: '', commentaire: '' },
        precommandes_autorisees: data.precommandes_autorisees === true,
        rupture_commandable: data.rupture_commandable === true,
        livraison_partielle: data.livraison_partielle === true,
      };
      setFormData(loadedForm);
      setOriginalData(loadedForm);
    } catch (error) {
      // Requête annulée (AbortController) → ignorer silencieusement
      if (error.name === 'CanceledError' || error.name === 'AbortError' || signal?.aborted) return;
      // Réponse obsolète (org a changé) → ignorer
      if (orgIdRef.current !== orgId) return;
      if (error.response?.status === 404) toast.error('Aucune fiche entreprise liée à votre compte');
      else toast.error('Erreur lors du chargement de la fiche');
    } finally {
      // Ne mettre à jour loading que si la requête est encore pertinente
      if (!signal?.aborted && orgIdRef.current === orgId) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingBanner;
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, [type]: imageUrl }));
      toast.success(`${type === 'logo' ? 'Logo' : 'Bannière'} uploadé avec succès`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { id: Date.now().toString(), nom: '', email: '', telephone: '', fonction: '' }]
    }));
  };

  const removeContact = (contactId) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.filter(c => c.id !== contactId) }));
  };

  const updateContact = (contactId, field, value) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.map(c => c.id === contactId ? { ...c, [field]: value } : c) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) { toast.error('Le nom de l\'entreprise est obligatoire'); return; }
    const invalidContact = formData.contacts.find(c => !c.nom?.trim());
    if (invalidContact) { toast.error('Chaque contact doit avoir un nom'); return; }

    // Capturer l'org au moment du submit (protection contre switch pendant la sauvegarde)
    const submitOrgId = orgIdRef.current;
    setSaving(true);
    try {
      const cleanedContacts = formData.contacts.map(c => ({
        id: c.id, nom: c.nom?.trim(), email: c.email?.trim() || null, telephone: c.telephone?.trim() || null, fonction: c.fonction?.trim() || null,
      }));

      const putParams = submitOrgId ? { org_id: submitOrgId } : {};
      await api.put('/partners/me', {
        nom: formData.nom.trim(),
        description: formData.description,
        logo: formData.logo,
        banniere: formData.banniere,
        contacts: cleanedContacts,
        contact_email: cleanedContacts[0]?.email || null,
        contact_telephone: cleanedContacts[0]?.telephone || null,
        conditions_achat: formData.conditions_achat,
        process_commande: formData.process_commande,
        shipping_rules: (formData.shipping_rules || []).filter(r => r.zone),
        payment_terms: {
          mode_paiement: formData.payment_terms?.mode_paiement || null,
          delai_jours: formData.payment_terms?.delai_jours ? parseInt(formData.payment_terms.delai_jours) : null,
          commentaire: formData.payment_terms?.commentaire || null,
          escompte_percent: formData.payment_terms?.escompte_percent ? parseFloat(formData.payment_terms.escompte_percent) : null,
          encours_max: formData.payment_terms?.encours_max ? parseFloat(formData.payment_terms.encours_max) : null,
        },
        precommandes_autorisees: formData.precommandes_autorisees,
        rupture_commandable: formData.rupture_commandable,
        livraison_partielle: formData.livraison_partielle,
      }, { params: putParams });
      toast.success('Fiche entreprise mise à jour');
      // Recharger uniquement si l'org n'a pas changé pendant la sauvegarde
      if (orgIdRef.current === submitOrgId) {
        await loadPartnerData(submitOrgId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <DashboardWrapper><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></DashboardWrapper>;
  if (!partner) return (
    <DashboardWrapper>
      <div className="text-center py-12">
        <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Aucune fiche entreprise</h2>
        <p className="text-muted-foreground">Votre compte n'est pas lié à une fiche partenaire. Contactez l'administrateur.</p>
      </div>
    </DashboardWrapper>
  );

  const capLabels = { distributor: 'Distributeur', publisher: 'Éditeur', other: 'Autre' };
  const capIcons = { distributor: Building2, publisher: Gamepad2, other: Settings2 };

  return (
    <DashboardWrapper>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="partner-entreprise-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="h-6 w-6" /> Mon Entreprise
            </h1>
            <p className="text-muted-foreground">Gérez les informations de votre fiche partenaire</p>
          </div>
          {partner?.id && (
            <Button
              variant="outline" size="sm"
              onClick={() => window.open(`/dashboard/partner-preview/${partner.id}`, '_blank')}
              className="gap-1.5 text-blue-600 hover:text-blue-800 border-blue-200 hover:bg-blue-50"
              data-testid="view-as-retailer-btn"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Voir comme Retailer
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ═══ 1. Informations Générales ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
              <Building2 className="h-4 w-4" /> Informations générales
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="nom">Nom du partenaire *</Label>
                <Input id="nom" name="nom" value={formData.nom} onChange={handleChange} required data-testid="input-partner-nom" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Slug (URL)</Label>
                <Input value={partner.slug || ''} disabled className="bg-gray-100 cursor-not-allowed" data-testid="slug-readonly" />
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-muted-foreground">Géré par l'administrateur</p>
                  <Badge variant="outline" className="text-[10px]">Lecture seule</Badge>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" name="description" value={formData.description} onChange={handleChange} placeholder="Présentez votre entreprise..." rows={3} data-testid="input-partner-description" />
            </div>

            {/* Read-only: Partenaire actif */}
            <div className="flex items-center gap-2 opacity-60 cursor-not-allowed" data-testid="is-active-readonly">
              <Switch id="is_active_ro" checked={partner.is_active !== false} disabled />
              <Label htmlFor="is_active_ro" className="cursor-not-allowed">Partenaire actif</Label>
              <Badge variant="outline" className="text-[10px] ml-1">Lecture seule</Badge>
            </div>

            {/* Read-only: Rôles métier */}
            <div className="space-y-2" data-testid="capabilities-readonly">
              <Label className="font-semibold">Rôles métier (capabilities)</Label>
              <div className="flex flex-wrap gap-3">
                {(partner.capabilities || ['other']).map(cap => {
                  const Icon = capIcons[cap] || Settings2;
                  return (
                    <div key={cap} className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-gray-50 border-gray-200 opacity-70 cursor-not-allowed">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{capLabels[cap] || cap}</span>
                    </div>
                  );
                })}
                <Badge variant="outline" className="text-[10px] self-center">Lecture seule</Badge>
              </div>
            </div>

            {/* Read-only: Partenaire commercial */}
            <div className="flex items-center gap-2 opacity-60 cursor-not-allowed" data-testid="is-partner-readonly">
              <Switch id="is_partner_ro" checked={partner.is_partner === true} disabled />
              <Label htmlFor="is_partner_ro" className="flex items-center gap-1.5 cursor-not-allowed">
                <RosetteIcon className="w-5 h-5 text-amber-500" /> Partenaire commercial
              </Label>
              <Badge variant="outline" className="text-[10px] ml-1">Lecture seule</Badge>
            </div>
          </div>

          {/* ═══ 2. Visuels ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
              <ImageIcon className="h-4 w-4" /> Visuels
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="logo">Logo</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input id="logo" name="logo" value={formData.logo} onChange={handleChange} placeholder="https://..." className="flex-1" data-testid="input-partner-logo" />
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files?.[0], 'logo')} data-testid="upload-logo-input" />
                      <Button type="button" variant="outline" size="icon" disabled={uploadingLogo} asChild>
                        <span><Upload className={`h-4 w-4 ${uploadingLogo ? 'animate-pulse' : ''}`} /></span>
                      </Button>
                    </label>
                  </div>
                  {formData.logo && (
                    <div className="p-2 bg-gray-50 rounded border">
                      <img src={formData.logo} alt="Logo" className="max-h-16 object-contain mx-auto" onError={e => e.target.style.display='none'} />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="banniere">Bannière</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input id="banniere" name="banniere" value={formData.banniere} onChange={handleChange} placeholder="https://..." className="flex-1" data-testid="input-partner-banniere" />
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files?.[0], 'banniere')} data-testid="upload-banner-input" />
                      <Button type="button" variant="outline" size="icon" disabled={uploadingBanner} asChild>
                        <span><Upload className={`h-4 w-4 ${uploadingBanner ? 'animate-pulse' : ''}`} /></span>
                      </Button>
                    </label>
                  </div>
                  {formData.banniere && (
                    <div className="p-2 bg-gray-50 rounded border">
                      <img src={formData.banniere} alt="Bannière" className="max-h-16 w-full object-cover rounded" onError={e => e.target.style.display='none'} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ═══ 3. Conditions de livraison ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
              <MapPin className="h-4 w-4" /> Conditions de livraison
            </h3>
            <p className="text-xs text-muted-foreground">Données structurées pour le calcul automatique du franco et des frais de port par zone.</p>

            {(formData.shipping_rules || []).map((rule, ri) => (
              <div key={rule.id || ri} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-testid={`shipping-rule-${ri}`}>
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs">Zone {ri + 1}</Badge>
                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => {
                    const rules = [...(formData.shipping_rules || [])]; rules.splice(ri, 1);
                    setFormData({ ...formData, shipping_rules: rules });
                  }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px]">Zone *</Label>
                    <Select value={rule.zone} onValueChange={(v) => { const r=[...(formData.shipping_rules||[])]; r[ri]={...r[ri],zone:v}; setFormData({...formData,shipping_rules:r}); }}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`shipping-zone-${ri}`}><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="france_metropolitaine">France métropolitaine</SelectItem>
                        <SelectItem value="belgique">Belgique</SelectItem>
                        <SelectItem value="suisse">Suisse</SelectItem>
                        <SelectItem value="luxembourg">Luxembourg</SelectItem>
                        <SelectItem value="dom_tom">DOM-TOM</SelectItem>
                        <SelectItem value="europe">Europe</SelectItem>
                        <SelectItem value="international">International</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[10px]">Franco (€)</Label><Input type="number" step="0.01" className="h-8 text-xs" value={rule.franco ?? ''} onChange={e=>{const r=[...(formData.shipping_rules||[])];r[ri]={...r[ri],franco:e.target.value?parseFloat(e.target.value):null};setFormData({...formData,shipping_rules:r});}} placeholder="350" data-testid={`shipping-franco-${ri}`} /></div>
                  <div><Label className="text-[10px]">Min. commande (€)</Label><Input type="number" step="0.01" className="h-8 text-xs" value={rule.minimum_commande ?? ''} onChange={e=>{const r=[...(formData.shipping_rules||[])];r[ri]={...r[ri],minimum_commande:e.target.value?parseFloat(e.target.value):null};setFormData({...formData,shipping_rules:r});}} placeholder="150" data-testid={`shipping-min-${ri}`} /></div>
                  <div><Label className="text-[10px]">Frais de port fixe (€)</Label><Input type="number" step="0.01" className="h-8 text-xs" value={rule.frais_port_fixe ?? ''} onChange={e=>{const r=[...(formData.shipping_rules||[])];r[ri]={...r[ri],frais_port_fixe:e.target.value?parseFloat(e.target.value):null};setFormData({...formData,shipping_rules:r});}} placeholder="12.50" data-testid={`shipping-fixe-${ri}`} /></div>
                  <div><Label className="text-[10px]">Frais de port % du total</Label><Input type="number" step="0.1" className="h-8 text-xs" value={rule.frais_port_percent ?? ''} onChange={e=>{const r=[...(formData.shipping_rules||[])];r[ri]={...r[ri],frais_port_percent:e.target.value?parseFloat(e.target.value):null};setFormData({...formData,shipping_rules:r});}} placeholder="3.5" data-testid={`shipping-pct-${ri}`} /></div>
                  <div><Label className="text-[10px]">Délai expédition moyen* (jours)</Label><Input type="number" min="0" step="1" className="h-8 text-xs" value={rule.delai_expedition_jours ?? ''} onChange={e=>{const r=[...(formData.shipping_rules||[])];r[ri]={...r[ri],delai_expedition_jours:e.target.value?parseInt(e.target.value):null};setFormData({...formData,shipping_rules:r});}} placeholder="3" data-testid={`shipping-delai-${ri}`} /></div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => {
              setFormData({ ...formData, shipping_rules: [...(formData.shipping_rules || []), { id: Date.now().toString(), zone: '', minimum_commande: null, franco: null, frais_port_fixe: null, frais_port_percent: null, delai_expedition_jours: null }] });
            }} data-testid="add-shipping-rule"><Plus className="h-3 w-3 mr-1" /> Ajouter une zone</Button>
          </div>

          {/* ═══ 4. Indicateurs de distribution ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
              <PackageCheck className="h-4 w-4" /> Indicateurs de distribution
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <Switch id="precommandes" checked={formData.precommandes_autorisees} onCheckedChange={v => setFormData({...formData, precommandes_autorisees: v})} data-testid="switch-precommandes" />
                <Label htmlFor="precommandes" className="text-xs font-medium cursor-pointer">Précommandes</Label>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <Switch id="rupture" checked={formData.rupture_commandable} onCheckedChange={v => setFormData({...formData, rupture_commandable: v})} data-testid="switch-rupture" />
                <Label htmlFor="rupture" className="text-xs font-medium cursor-pointer">Rupture commandable</Label>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <Switch id="partielle" checked={formData.livraison_partielle} onCheckedChange={v => setFormData({...formData, livraison_partielle: v})} data-testid="switch-livraison-partielle" />
                <Label htmlFor="partielle" className="text-xs font-medium cursor-pointer">Livraison partielle</Label>
              </div>
            </div>
          </div>

          {/* ═══ 5. Conditions d'Achats ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
              <ShoppingCart className="h-4 w-4" /> Conditions d'Achats
            </h3>
            <Textarea id="conditions_achat" name="conditions_achat" value={formData.conditions_achat} onChange={handleChange} placeholder="Détaillez les conditions d'achat..." rows={3} data-testid="input-partner-conditions-achat" />
          </div>

          {/* ═══ 6. Conditions de Paiement ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
              <Euro className="h-4 w-4" /> Conditions de Paiement
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Mode de paiement</Label><Input value={formData.payment_terms?.mode_paiement ?? ''} onChange={e => setFormData({...formData, payment_terms: {...formData.payment_terms, mode_paiement: e.target.value}})} placeholder="Ex: Virement" data-testid="input-payment-mode" /></div>
              <div><Label className="text-xs">Délai paiement (jours)</Label><Input type="number" min="0" step="1" value={formData.payment_terms?.delai_jours ?? ''} onChange={e => setFormData({...formData, payment_terms: {...formData.payment_terms, delai_jours: e.target.value}})} placeholder="30" data-testid="input-payment-delai" /></div>
              <div><Label className="text-xs">Commentaire paiement</Label><Input value={formData.payment_terms?.commentaire ?? ''} onChange={e => setFormData({...formData, payment_terms: {...formData.payment_terms, commentaire: e.target.value}})} placeholder="Ex: Fin de mois" data-testid="input-payment-commentaire" /></div>
              <div><Label className="text-xs">Escompte %</Label><Input type="number" step="0.1" value={formData.payment_terms?.escompte_percent ?? ''} onChange={e => setFormData({...formData, payment_terms: {...formData.payment_terms, escompte_percent: e.target.value ? parseFloat(e.target.value) : null}})} placeholder="2.0" data-testid="input-payment-escompte" /></div>
              <div><Label className="text-xs">Encours max (€)</Label><Input type="number" step="100" value={formData.payment_terms?.encours_max ?? ''} onChange={e => setFormData({...formData, payment_terms: {...formData.payment_terms, encours_max: e.target.value ? parseFloat(e.target.value) : null}})} placeholder="15000" data-testid="input-payment-encours" /></div>
            </div>
          </div>

          {/* ═══ 7. Process de Commande ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
              <FileText className="h-4 w-4" /> Process de Commande
            </h3>
            <Textarea id="process_commande" name="process_commande" value={formData.process_commande} onChange={handleChange} placeholder="Décrivez le processus de commande..." rows={3} data-testid="input-partner-process-commande" />
          </div>

          {/* ═══ 8. Contacts ═══ */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-medium flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" /> Contacts
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="add-contact-button">
                <Plus className="h-3 w-3 mr-1" /> Ajouter
              </Button>
            </div>

            {formData.contacts.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <User className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-muted-foreground">Aucun contact</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.contacts.map((contact, idx) => (
                  <div key={contact.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`contact-card-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="text-xs">Contact {idx + 1}</Badge>
                      <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => removeContact(contact.id)} data-testid={`remove-contact-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label className="text-xs flex items-center gap-1"><User className="h-3 w-3" /> Nom *</Label><Input value={contact.nom} onChange={e => updateContact(contact.id, 'nom', e.target.value)} placeholder="Prénom Nom" data-testid={`contact-nom-${idx}`} /></div>
                      <div><Label className="text-xs flex items-center gap-1"><Briefcase className="h-3 w-3" /> Fonction</Label><Input value={contact.fonction || ''} onChange={e => updateContact(contact.id, 'fonction', e.target.value)} placeholder="Directeur commercial" data-testid={`contact-fonction-${idx}`} /></div>
                      <div><Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label><Input type="email" value={contact.email || ''} onChange={e => updateContact(contact.id, 'email', e.target.value)} placeholder="contact@entreprise.com" data-testid={`contact-email-${idx}`} /></div>
                      <div><Label className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> Téléphone</Label><Input value={contact.telephone || ''} onChange={e => updateContact(contact.id, 'telephone', e.target.value)} placeholder="01 23 45 67 89" data-testid={`contact-telephone-${idx}`} /></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-2 pb-6">
            <Button type="submit" size="lg" disabled={saving || !canEditProfile} className="min-w-[200px]" data-testid="save-partner-button">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement...</> : <><Save className="h-4 w-4 mr-2" />Enregistrer les modifications</>}
            </Button>
          </div>
        </form>
      </div>
      <NavigationBlockerDialog blocker={blocker} />
    </DashboardWrapper>
  );
};

export default PartnerEntreprise;
